import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import GenericSelect from '../../../components/ui/GenericSelect';
import useLayoutBucket from '../../../hooks/useLayoutBucket';
import { SURVEY_DIMENSIONS, fetchSurveyGroups, formatNumber } from '../config';
import {
  Field, FilterRow, Note, StateBox, TableWrap,
} from '../styles';

// PM analitik P7b — anonim anket grup toplulaştırması (soru × departman / ünvan / yönetici). n < k hücre ve onu geri getirecek
// tamamlayıcı hücre gizlenir; bireysel satır ve kişi bazlı katılım yoktur (analiz §32 "Ankette bireysel drill-down bulunmaz").
export default function SurveyGroups({ filters }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const bucket = useLayoutBucket();
  const narrow = bucket === 'xs' || bucket === 'sm';
  const [dimension, setDimension] = useState('department');
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [attempt, setAttempt] = useState(0);
  const [height, setHeight] = useState(420);
  const ref = useRef(null);
  const seq = useRef(0);
  const key = JSON.stringify({ filters, dimension });

  useEffect(() => {
    if (!filters.periodId) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetchSurveyGroups(filters, dimension)
      .then((data) => { if (mine === seq.current) setState({ loading: false, error: false, data }); })
      .catch(() => { if (mine === seq.current) setState({ loading: false, error: true, data: null }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Satırlar ekrana hazır metinle kurulur (gizli hücrenin sayısı hiç gelmez — "Gizlendi" yazılır).
  const raw = useMemo(() => state.data || [], [state.data]);
  const unavailable = raw.length > 0 && raw.every((r) => r.suppressionReason === 'performance.experience.surveyFilterNotApplicable');
  const rows = useMemo(() => raw.map((r, i) => {
    const hidden = !!r.suppressionReason;
    const n = (v) => (hidden || v == null ? '-' : formatNumber(v, lang, 0));
    return {
      rowId: `${r.groupKey || 'none'}-${r.question}-${i}`,
      groupLabel: r.groupLabel || t('performance.experience.groups.unassigned'),
      question: t(`performance.experience.question.${r.question}.short`),
      validN: hidden ? t('performance.experience.suppressed.small') : formatNumber(r.validN, lang, 0),
      positivePct: hidden || r.positivePct == null ? '-' : `${formatNumber(r.positivePct, lang)}%`,
      score1: n(r.score1), score2: n(r.score2), score3: n(r.score3), score4: n(r.score4), score5: n(r.score5),
      suppressed: hidden,
    };
  }), [raw, t, lang]);

  const columns = useMemo(() => ['groupLabel', 'question', 'validN', 'positivePct', 'score1', 'score2', 'score3', 'score4', 'score5']
    .map((name) => ({ name, label: t(`performance.experience.groups.col.${name}`), options: { filter: false, sort: false } })), [t]);

  return (
    <>
      <FilterRow data-section="group-filters">
        <Field>
          <GenericSelect
            label={t('performance.experience.groups.dimension')}
            value={dimension}
            onChange={(v) => setDimension(v || 'department')}
            options={SURVEY_DIMENSIONS.map((d) => ({ id: d, name: t(`performance.experience.groups.dimensionValue.${d}`) }))}
            clearable={false}
          />
        </Field>
      </FilterRow>
      <Note>{t('performance.experience.groups.note')}</Note>
      <TableWrap ref={ref}>
        {state.error ? (
          <StateBox data-state="groups-error">
            <span>{t('performance.experience.loadFailed')}</span>
            <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry-groups">{t('performance.experience.retry')}</Button>
          </StateBox>
        ) : unavailable ? (
          <StateBox data-state="survey-unavailable">{t('performance.experience.surveyFilterNotApplicable')}</StateBox>
        ) : (
          <DataTable
            title={t('performance.experience.groups.title')}
            columns={columns}
            data={rows}
            fetching={state.loading}
            serverSide={false}
            disableFilters
            canCreateNewRecord={false}
            customIdSelector="rowId"
            tableBodyMaxHeight={narrow ? 'none' : `${Math.max(height - 170, 160)}px`}
          />
        )}
      </TableWrap>
    </>
  );
}
